import { useCallback, useEffect, useState } from 'react';
import {
  createPoint,
  listPoints,
  qrImage,
  siteQrInfo,
  updatePoint,
  type FeedbackPoint,
  type SiteQr,
} from '../api';
import type { FeedbackTranslate } from '../i18n';
import { Button, Card, DataTable, Field, Notice, Select, StatusBadge, TextInput, type Column } from '../../../shell/kit';
import { useResource } from '../../../shell/hooks';
import { sites as loadSites } from '../../../api';
import { useToast } from '../../../shell/toast';
import { errorText } from '../../../shell/errors';
import { downloadBlob } from '../../../shell/download';

/**
 * Печать QR-кодов (М5.1). Картинку рисует платформа: раньше экран отдавал
 * только ссылку и токен, и управляющий шёл во внешний генератор — со своим
 * уровнем коррекции и без гарантии, что код ведёт на форму. Здесь — код
 * объекта и коды точек внутри него, SVG для типографии и PNG для печати.
 */
export function QrView({ t, siteId }: { t: FeedbackTranslate; siteId: string | null }) {
  const siteList = useResource(() => loadSites(), []);
  const [target, setTarget] = useState(siteId ?? '');
  const [info, setInfo] = useState<SiteQr | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [points, setPoints] = useState<readonly FeedbackPoint[]>([]);
  const [pointName, setPointName] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  const options = siteList.data ?? [];
  const selected = target || siteId || options[0]?.id || '';

  const fail = useCallback(
    (err: unknown) => {
      const shown = errorText(err);
      toast.error(shown.message, shown.code);
    },
    [toast],
  );

  const load = useCallback(async () => {
    if (!selected) return;
    try {
      const next = await siteQrInfo(selected);
      setInfo(next);
      const image = await qrImage(next.imagePath, 'svg');
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(image.blob);
      });
      setPoints((await listPoints(selected)).items);
    } catch (err) {
      fail(err);
    }
  }, [selected, fail]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview],
  );

  const download = async (path: string, format: 'svg' | 'png'): Promise<void> => {
    try {
      const image = await qrImage(path, format);
      downloadBlob(image.filename, image.blob);
    } catch (err) {
      fail(err);
    }
  };

  const act = async (fn: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    try {
      await fn();
      toast.ok(t('feedback.common.saved'));
      setPoints((await listPoints(selected)).items);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const columns: ReadonlyArray<Column<FeedbackPoint>> = [
    {
      key: 'name',
      header: t('feedback.qr.pointName'),
      render: (p) => (
        <span className="flex items-center gap-2">
          {p.name}
          {p.status === 'archived' ? <StatusBadge tone="neutral">{t('feedback.qr.pointArchived')}</StatusBadge> : null}
        </span>
      ),
    },
    { key: 'n', header: t('feedback.qr.pointFeedback'), numeric: true, render: (p) => p.feedbackCount },
    {
      key: 'actions',
      header: '',
      render: (p) =>
        p.status === 'active' ? (
          <span className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void download(`/api/v1/feedback/points/${p.id}/image`, 'svg')}>
              SVG
            </Button>
            <Button size="sm" onClick={() => void download(`/api/v1/feedback/points/${p.id}/image`, 'png')}>
              PNG
            </Button>
            <Button
              size="sm"
              disabled={busy}
              title={t('feedback.qr.pointRotateHint')}
              onClick={() => void act(() => updatePoint(p.id, { rotateToken: true }))}
            >
              {t('feedback.qr.pointRotate')}
            </Button>
            <Button size="sm" tone="danger" disabled={busy} onClick={() => void act(() => updatePoint(p.id, { status: 'archived' }))}>
              {t('feedback.qr.pointArchive')}
            </Button>
          </span>
        ) : (
          <Button size="sm" disabled={busy} onClick={() => void act(() => updatePoint(p.id, { status: 'active' }))}>
            {t('feedback.qr.pointRestore')}
          </Button>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <Notice tone="info">{t('feedback.qr.hint')}</Notice>

      <Card title={t('feedback.qr.title')}>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-72 flex-1">
            <Field label={t('feedback.qr.site')}>
              <Select
                value={selected}
                disabled={options.length === 0}
                onChange={(e) => {
                  setTarget(e.target.value);
                  // Код принадлежит объекту: старый рядом с новым выбором — прямой
                  // путь к наклейке не на том посту.
                  setInfo(null);
                  setPreview(null);
                }}
              >
                {options.map((site) => (
                  <option key={site.id} value={site.id}>
                    {site.name} · {site.code}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          {info ? (
            <>
              <Button tone="primary" onClick={() => void download(info.imagePath, 'svg')}>
                {t('feedback.qr.downloadSvg')}
              </Button>
              <Button onClick={() => void download(info.imagePath, 'png')}>{t('feedback.qr.downloadPng')}</Button>
            </>
          ) : null}
        </div>

        {info ? (
          <div className="mt-4 flex flex-wrap gap-6">
            {preview ? (
              <figure className="rounded border border-line bg-white p-2">
                <img src={preview} alt={t('feedback.qr.preview')} className="h-56 w-56 object-contain" />
                <figcaption className="mt-1 text-center text-xs text-ink-muted">{t('feedback.qr.preview')}</figcaption>
              </figure>
            ) : null}
            <dl className="min-w-64 flex-1 space-y-1.5 text-sm">
              <div className="flex gap-2">
                <dt className="w-40 text-ink-muted">{t('feedback.qr.path')}</dt>
                <dd>
                  <a href={info.formPath} target="_blank" rel="noreferrer" className="break-all text-brand-700 underline underline-offset-2">
                    {info.formUrl}
                  </a>
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="w-40 text-ink-muted">{t('feedback.qr.token')}</dt>
                <dd>
                  <code className="rounded bg-sunken px-2 py-0.5 text-xs">{info.token}</code>
                </dd>
              </div>
            </dl>
          </div>
        ) : null}
      </Card>

      <Card title={t('feedback.qr.points')} hint={t('feedback.qr.pointsHint')}>
        <div className="mb-3 flex flex-wrap items-end gap-2">
          <div className="min-w-64 flex-1">
            <Field label={t('feedback.qr.pointName')}>
              <TextInput value={pointName} maxLength={80} onChange={(e) => setPointName(e.target.value)} />
            </Field>
          </div>
          <Button
            tone="primary"
            disabled={busy || !selected || pointName.trim().length === 0}
            onClick={() =>
              void act(async () => {
                await createPoint(selected, pointName.trim());
                setPointName('');
              })
            }
          >
            {t('feedback.qr.pointAdd')}
          </Button>
        </div>
        <DataTable rows={points} columns={columns} empty={t('feedback.qr.none')} rowKey={(p) => p.id} />
      </Card>
    </div>
  );
}
